import { Button, Pagination, Spin } from "antd";
import {
  Check,
  CheckSquare,
  Clock,
  FileSignature,
  Square,
  Truck,
  User,
} from "lucide-react";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { useDebounceValue } from "~/hooks/use-debounce";
import { useGetBookings } from "~/services/booking/booking.queries";
import { TBooking } from "~/types/booking";
import { TServiceTicket } from "~/types";
import { capitalize } from "~/utils";
import { ServiceType, SortOrder } from "~/utils/constants";
import { PrintWaybill } from "./print-waybill";

interface PrintByBookingsProps {
  selectedDate: string;
  setSelectedDate: Dispatch<SetStateAction<string>>;
  selectedVessel: string;
  setSelectedVessel: Dispatch<SetStateAction<string>>;
  selectedClient: string;
  setSelectedClient: Dispatch<SetStateAction<string>>;
  compactView: boolean;
  setCompactView: Dispatch<SetStateAction<boolean>>;
  printOrder: string;
}

export const PrintByBookings: React.FC<PrintByBookingsProps> = ({
  selectedDate,
  selectedClient,
  compactView,
  printOrder,
}) => {
  const [page, setPage] = useState(1);
  const [searchTerm] = useState("");
  const [search] = useDebounceValue(searchTerm, 500);
  const [selectedServiceTickets, setSelectedServiceTickets] = useState<
    string[]
  >([]);

  const filterParams = useMemo(() => {
    const params: Record<string, any> = {
      page,
      pageSize: 10,
    };
    if (search) {
      params.search = search;
    }

    if (selectedClient !== "all") {
      params.clientId = selectedClient;
    }

    return params;
  }, [page, search, selectedClient]);

  const { data, isLoading } = useGetBookings({
    page,
    pageSize: 10,
    sortByCreatedAt: SortOrder.DESC,
    ...filterParams,
  });

  const sortOrder = printOrder === "title" ? "title" : "inventoryRef";

  const selectedServiceTicketsForPrint = useMemo(() => {
    return (
      data?.items.flatMap((booking) =>
        booking.serviceTicket
          .filter((ticket) => selectedServiceTickets.includes(ticket.id))
          .map((ticket) => ({
            ...ticket,
            clientService: ticket.client,
            booking,
          }))
      ) || []
    );
  }, [data, selectedServiceTickets]);

  // Toggle selection for a service ticket
  const toggleServiceTicketSelection = (serviceTicketId: string) => {
    setSelectedServiceTickets((prev) =>
      prev.includes(serviceTicketId)
        ? prev.filter((id) => id !== serviceTicketId)
        : [...prev, serviceTicketId]
    );
  };

  // Select all service tickets within a booking
  const toggleAllServiceTicketsInBooking = (booking: TBooking) => {
    const ticketIds = booking.serviceTicket.map((ticket) => ticket.id);
    setSelectedServiceTickets((prev) => {
      const allSelected = ticketIds.every((id) => prev.includes(id));
      return allSelected
        ? prev.filter((id) => !ticketIds.includes(id))
        : [...new Set([...prev, ...ticketIds])];
    });
  };

  const getCompletionStatusIndicators = (ticket: TServiceTicket) => {
    const indicators = [];

    if (ticket.timeIn && ticket.timeOut) {
      indicators.push(
        <div
          key="time"
          className="flex items-center space-x-1 text-xs text-green-600"
        >
          <Clock size={10} />
          <span>Time</span>
        </div>
      );
    }

    if (ticket.clientSignature) {
      indicators.push(
        <div
          key="signature"
          className="flex items-center space-x-1 text-xs text-green-600"
        >
          <FileSignature size={10} />
          <span>Signed</span>
        </div>
      );
    }

    if (ticket.staffCheckoff) {
      indicators.push(
        <div
          key="checkoff"
          className="flex items-center space-x-1 text-xs text-green-600"
        >
          <User size={10} />
          <span>Confirmed</span>
        </div>
      );
    }

    return indicators;
  };

  return (
    <div className="mb-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-medium">Bookings for {selectedDate}</h2>
        <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-500">
            {selectedServiceTickets.length} service tickets selected
          </div>
          <PrintWaybill
            selectedServiceTicketsForPrint={selectedServiceTicketsForPrint}
            sortOrder={sortOrder}
          />
        </div>
      </div>
      <Spin spinning={isLoading} size="large">
        <div className="space-y-4">
          {(data?.meta.totalItems ?? 0) > 0 ? (
            data?.items.map((booking) => (
              <div
                key={booking.id}
                className="bg-white rounded-lg border shadow-sm overflow-hidden"
              >
                <div className="p-4 border-b flex justify-between items-center">
                  <div className="flex flex-col md:flex-row md:items-center md:space-x-6">
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">
                          {booking.referenceNumber}
                        </span>
                      </div>
                      <div className="text-gray-600 font-medium mt-1">
                        {booking.client?.companyName ||
                          [booking.client?.firstName, booking.client?.lastName]
                            .filter(Boolean)
                            .join(" ")
                            .trim() ||
                          "Unnamed Client"}
                      </div>
                    </div>

                    <div className="mt-2 md:mt-0">
                      <div className="flex items-center space-x-1 text-gray-500 text-sm">
                        <Clock size={14} />
                        <span>
                          {booking.serviceTicket[0]?.timeIn
                            ? new Date(
                                booking.serviceTicket[0].timeIn
                              ).toLocaleTimeString()
                            : "No time"}{" "}
                          -
                          {booking.serviceTicket[0]?.timeOut
                            ? new Date(
                                booking.serviceTicket[0].timeOut
                              ).toLocaleTimeString()
                            : "No time"}
                        </span>
                      </div>
                      <div className="flex items-center space-x-1 text-gray-500 text-sm mt-1">
                        <Truck size={14} />
                        <span>
                          {booking.serviceTicket[0]?.vessel?.name ||
                            "No vessel"}
                        </span>
                        <span className="mx-1">•</span>
                        <User size={14} />
                        <span>
                          {booking.serviceTicket[0]?.staffs &&
                          booking.serviceTicket[0].staffs.length > 0 ? (
                            booking.serviceTicket[0].staffs.length <= 2 ? (
                              booking.serviceTicket[0].staffs.map(
                                (staff, index) => (
                                  <span key={index}>
                                    {staff?.user?.firstName}{" "}
                                    {staff?.user?.lastName}
                                    {index <
                                      booking.serviceTicket[0].staffs!.length -
                                        1 && ", "}
                                  </span>
                                )
                              )
                            ) : (
                              <>
                                {booking.serviceTicket[0].staffs
                                  .slice(0, 2)
                                  .map((staff, index) => (
                                    <span key={index}>
                                      {staff?.user?.firstName}{" "}
                                      {staff?.user?.lastName}
                                      {index < 1 && ", "}
                                    </span>
                                  ))}
                                <span>
                                  {" "}
                                  + {booking.serviceTicket[0].staffs.length - 2}
                                </span>
                              </>
                            )
                          ) : (
                            "No staff assigned"
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    className="p-1 hover:bg-gray-100 rounded"
                    onClick={() => toggleAllServiceTicketsInBooking(booking)}
                    title="Select all service tickets in this booking"
                  >
                    {booking.serviceTicket.every((ticket) =>
                      selectedServiceTickets.includes(ticket.id)
                    ) ? (
                      <CheckSquare size={16} className="text-blue-500" />
                    ) : (
                      <Square size={16} className="text-gray-400" />
                    )}
                  </button>
                </div>

                <div className="p-4">
                  {compactView ? (
                    <div className="flex flex-wrap gap-2">
                      {booking.serviceTicket.map((ticket) => (
                        <div
                          key={ticket.id}
                          className={`flex items-center space-x-2 px-3 py-2 rounded-lg border ${
                            selectedServiceTickets.includes(ticket.id)
                              ? "border-blue-500 bg-blue-50"
                              : "hover:bg-gray-50"
                          }`}
                          onClick={() =>
                            toggleServiceTicketSelection(ticket.id)
                          }
                        >
                          {selectedServiceTickets.includes(ticket.id) ? (
                            <Check size={14} className="text-blue-500" />
                          ) : (
                            <Square size={14} className="text-gray-400" />
                          )}
                          <span className="font-medium">{ticket.serial}</span>
                          <span
                            className={`px-2 py-1 rounded-full text-xs ${
                              ticket.serviceType === ServiceType.COLLECTION
                                ? "bg-blue-100 text-blue-800"
                                : ticket.serviceType === ServiceType.DELIVERY
                                ? "bg-green-100 text-green-800"
                                : ticket.serviceType ===
                                  ServiceType.FABRICATION_SERVICE
                                ? "bg-purple-100 text-purple-800"
                                : ticket.serviceType ===
                                  ServiceType.ON_SITE_SERVICE
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {capitalize(ticket.serviceType)}
                          </span>
                          <div className="flex items-center space-x-1 ml-2">
                            {getCompletionStatusIndicators(ticket)}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {booking.serviceTicket.map((ticket) => (
                        <div
                          key={ticket.id}
                          className="p-3 border rounded-lg hover:bg-gray-50"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <button
                                className="mr-3"
                                onClick={() =>
                                  toggleServiceTicketSelection(ticket.id)
                                }
                              >
                                {selectedServiceTickets.includes(ticket.id) ? (
                                  <CheckSquare
                                    size={16}
                                    className="text-blue-500"
                                  />
                                ) : (
                                  <Square size={16} className="text-gray-400" />
                                )}
                              </button>
                              <span className="font-medium">
                                {ticket.serial}
                              </span>
                              <span
                                className={`px-2 py-1 rounded-full text-xs ${
                                  ticket.serviceType === ServiceType.COLLECTION
                                    ? "bg-blue-100 text-blue-800"
                                    : ticket.serviceType ===
                                      ServiceType.DELIVERY
                                    ? "bg-green-100 text-green-800"
                                    : ticket.serviceType ===
                                      ServiceType.FABRICATION_SERVICE
                                    ? "bg-purple-100 text-purple-800"
                                    : ticket.serviceType ===
                                      ServiceType.ON_SITE_SERVICE
                                    ? "bg-yellow-100 text-yellow-800"
                                    : "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {capitalize(ticket.serviceType)}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2">
                              {getCompletionStatusIndicators(ticket)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="bg-white rounded-lg border p-8 text-center">
              <h3 className="text-lg font-medium text-gray-700">
                No bookings found
              </h3>
              <p className="text-gray-500 mt-1">
                Try adjusting your search or filter criteria
              </p>
            </div>
          )}
        </div>
      </Spin>

      <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
        <Pagination
          total={data?.meta.totalItems}
          current={page}
          onChange={(page) => setPage(page)}
          itemRender={(_, type, originalElement) => {
            if (type === "prev") {
              return <Button>Previous</Button>;
            }
            if (type === "next") {
              return <Button>Next</Button>;
            }
            return originalElement;
          }}
        />
      </div>
    </div>
  );
};